"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Check, ArrowRight, ArrowDown, Eye } from "lucide-react";
import { STATS, SUCCESS_RATE, parseStat } from "@/lib/stats";

interface HeroProps {
  title?: string;
  subtitle?: string;
  description?: string;
  primaryCta?: {
    text: string;
    href: string;
  };
  secondaryCta?: {
    text: string;
    href: string;
  };
  backgroundImage?: string;
  overlayOpacity?: number;
}

interface StatItem {
  number: number;
  suffix: string;
  label: string;
}

const StatCounter = ({ stat }: { stat: StatItem }) => {
  const [count, setCount] = useState(0);

  // Pas de garde-fou "monté" : un effet ne s'exécute jamais au rendu serveur,
  // donc le compteur part forcément de 0 côté client, sans divergence.
  useEffect(() => {
    let start = 0;
    const end = stat.number;
    const duration = 2500;
    const increment = end / (duration / 16);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [stat.number]);

  return (
    <motion.div
      className="bg-white/10 backdrop-blur-sm rounded-lg p-4"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.05, backgroundColor: "rgba(255, 255, 255, 0.15)" }}
    >
      <p className="text-3xl font-bold text-white" suppressHydrationWarning>
        {count}
        {stat.suffix}
      </p>
      <p className="text-sm text-blue-100">{stat.label}</p>
    </motion.div>
  );
};

export default function Hero({
  title = "Préparez l'année scolaire 2026-2027 — Rejoignez nos parcours annuels",
  subtitle = "C2E - CLUB DES ÉLÈVES ET ÉTUDIANTS",
  description = "Réussissez votre année scolaire 2026-2027 avec nos programmes, mentorat personnalisé et ateliers pratiques — Places limitées.",
  primaryCta = { text: "S'inscrire maintenant", href: "/join" },
  secondaryCta = { text: "Voir les programmes", href: "/activities" },
  backgroundImage = "/images/photos/fondCTA2.webp",
  overlayOpacity = 0.7,
}: HeroProps) {
  return (
    <section className="relative min-h-[600px] lg:h-[700px] flex items-start lg:items-center justify-center overflow-hidden pt-24 pb-16 lg:pt-0 lg:pb-0" suppressHydrationWarning>
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src={backgroundImage}
          alt="C2E Hero"
          fill
          className="object-cover"
          priority
          sizes="100vw"
        />
        {/* Overlay bleu */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-c2e-blue to-c2e-blue-light"
          style={{ opacity: overlayOpacity }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          suppressHydrationWarning
        >
          {/* Badge */}
          <motion.div
            className="mb-3 lg:mb-6 inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur-sm px-3 lg:px-4 py-1.5 lg:py-2 text-xs lg:text-sm font-medium text-white"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            suppressHydrationWarning
          >
            <span className="flex h-3 w-3 items-center" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-80" aria-hidden="true"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-700 ring-2 ring-white" aria-hidden="true"></span>
            </span>
            <span suppressHydrationWarning>Inscriptions ouvertes — Année scolaire 2026-2027</span>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            className="text-sm lg:text-base font-semibold text-white/90 mb-2 lg:mb-3 tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            {subtitle}
          </motion.p>

          {/* Title */}
          <motion.h1
            className="text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl xl:text-6xl mb-4 lg:mb-6 px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {title}
          </motion.h1>

          {/* Description - USP */}
          <motion.p
            className="mx-auto max-w-3xl text-base lg:text-lg xl:text-xl text-blue-50 mb-6 lg:mb-8 leading-relaxed px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {description}
          </motion.p>

          {/* Valeurs ajoutées - Bullet points */}
          <motion.div
            className="hidden sm:flex flex-wrap justify-center gap-2 lg:gap-4 mb-6 lg:mb-10 px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {[
              // Un résultat réel et vérifiable vaut mieux qu'une promesse
              // de garantie, qu'aucun accompagnement scolaire ne peut tenir.
              "100% au BEPC en 2026",
              "Tuteurs expérimentés",
              "Suivi personnalisé",
              "100% Transparence",
            ].map((feature) => (
              <motion.div
                key={feature}
                className="flex items-center gap-1.5 lg:gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 lg:px-4 py-1.5 lg:py-2"
                whileHover={{
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  scale: 1.05,
                }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                <Check className="h-4 w-4 lg:h-5 lg:w-5 text-green-300" />
                <span className="text-xs lg:text-sm font-medium text-white">
                  {feature}
                </span>
              </motion.div>
            ))}
          </motion.div>

          {/* CTAs */}
          <motion.div
            className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center px-4 lg:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href={primaryCta.href}
                className="group rounded-full bg-c2e-red px-6 lg:px-8 py-3 lg:py-4 text-sm lg:text-base font-semibold text-white shadow-xl hover:bg-c2e-red-light transition-all duration-200 flex items-center justify-center gap-2"
              >
                {primaryCta.text}
                <ArrowRight className="h-4 w-4 lg:h-5 lg:w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                href={secondaryCta.href}
                className="group rounded-full bg-white/20 backdrop-blur-sm border-2 border-white px-6 lg:px-8 py-3 lg:py-4 text-sm lg:text-base font-semibold text-white hover:bg-white hover:text-c2e-blue transition-all duration-200 flex items-center justify-center gap-2"
              >
                {secondaryCta.text}
                <Eye className="h-4 w-4 lg:h-5 lg:w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="mt-8 lg:mt-16 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:gap-4 px-4 lg:px-0"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            {/* Les valeurs viennent de src/data/stats.json : voir src/lib/stats.ts */}
            {[
              { value: STATS.experienceYears, label: "Années d'Expérience" },
              { value: STATS.students, label: "Élèves & Étudiants Accompagnés" },
              { value: STATS.tutors, label: "Tuteurs qualifiés" },
              { value: STATS.volunteerTeachers, label: "Enseignants Bénévoles" },
              { value: SUCCESS_RATE.value, label: SUCCESS_RATE.label },
              { value: STATS.eventsPerYear, label: "Événements/an" },
            ].map(({ value, label }) => (
              <StatCounter
                key={label}
                stat={{ ...parseStat(value), label }}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ArrowDown className="h-6 w-6 text-white" aria-hidden="true" />
      </div>
    </section>
  );
}
